/**
 * `<AssetField>` — II.E.2 (#60).
 *
 * Surfaces an `asset` value-kind fact: paste / drop / file-picker
 * upload, thumbnail preview, upload progress, alt-text input, and the
 * broken-state UI for an orphaned asset reference (whose backing R2
 * blob has gone away or whose id doesn't resolve in the viewer's
 * Workspace).
 *
 * The component is environment-agnostic: it accepts an `uploader`
 * callback that returns a `Promise<UploadedAsset>` so the same widget
 * works against the live worker, an in-memory mock, or a future
 * offline-replay queue.
 *
 * Accessibility:
 *   - Every interactive element is keyboard reachable (the drop zone
 *     is a `<label>` over a visually-hidden `<input type="file">`).
 *   - Errors render as text alongside the field with `role="alert"`.
 *   - The alt-text input is required to graduate the field from a
 *     visual-only baseline to an a11y baseline (the structured caption
 *     workflow lands in Phase 2 per the AC).
 *   - The reduced-motion media query suppresses progress-bar animation
 *     when the user prefers reduced motion.
 */
import { button, div, img, input, label, span, type Mountable } from '@llui/dom';

export interface UploadedAsset {
  /** Asset id (opaque to the UI). */
  id: string;
  /** Signed URL for thumbnail rendering. May be `null` for orphans. */
  url: string | null;
  /** Mime type as reported by the server. */
  mime?: string;
  /** Byte size, for debug surfaces. */
  size_bytes?: number;
}

export interface AssetFieldValue {
  /** Asset id; `null` when the field is empty. */
  asset_id: string | null;
  /** Resolved signed URL; `null` for empty or orphaned references. */
  url: string | null;
  /** Mime type, when known. */
  mime?: string;
  /** Alt text (Phase 1 baseline; structured captions land in Phase 2). */
  alt_text?: string;
}

export type AssetUploadProgress =
  | { kind: 'idle' }
  | { kind: 'uploading'; percent: number }
  | { kind: 'error'; message: string };

export interface AssetFieldMessages {
  /** Drop-zone instruction text. */
  dropPrompt: string;
  /** Button label that triggers the file picker. */
  chooseFile: string;
  /** Label for the alt-text input. */
  altTextLabel: string;
  /** Placeholder for the alt-text input. */
  altTextPlaceholder: string;
  /** Text rendered when the value is empty. */
  empty: string;
  /** Text rendered in the broken-state ribbon. */
  broken: string;
  /** Button label that clears the field. */
  remove: string;
  /** Heading announcing the field (used as `aria-label`). */
  heading: string;
}

export const DEFAULT_ASSET_FIELD_MESSAGES: AssetFieldMessages = {
  dropPrompt: 'Drag an image here, paste from the clipboard, or',
  chooseFile: 'Choose a file',
  altTextLabel: 'Alt text',
  altTextPlaceholder: 'Describe the image for accessibility',
  empty: 'No image set',
  broken: 'Asset reference broken — the image is unavailable.',
  remove: 'Remove image',
  heading: 'Image',
};

export interface AssetFieldOptions {
  value: AssetFieldValue;
  onChange: (next: AssetFieldValue) => void;
  /**
   * Upload a binary file. Resolves to an `UploadedAsset` on success;
   * rejects on validation failure. The component surfaces the
   * rejection message via `role="alert"`.
   */
  uploader: (file: Blob) => Promise<UploadedAsset>;
  /** Optional current progress state (for caller-driven progress). */
  progress?: AssetUploadProgress;
  /** Stable DOM id prefix; used for label ↔ input wiring. */
  id: string;
  mode?: 'edit' | 'read';
  messages?: Partial<AssetFieldMessages>;
  /** Optional `data-testid` prefix. */
  testid?: string;
  /**
   * Pass a media-query matcher (defaults to `window.matchMedia`) so the
   * `prefers-reduced-motion` check can be stubbed in tests.
   */
  matchMedia?: (query: string) => { matches: boolean };
}

function prefersReducedMotion(matcher?: (q: string) => { matches: boolean }): boolean {
  if (!matcher) {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  return matcher('(prefers-reduced-motion: reduce)').matches;
}

export function assetField(options: AssetFieldOptions): Mountable {
  const {
    value,
    onChange,
    uploader,
    progress = { kind: 'idle' },
    id,
    mode = 'edit',
    testid,
  } = options;
  const messages: AssetFieldMessages = {
    ...DEFAULT_ASSET_FIELD_MESSAGES,
    ...options.messages,
  };
  const reducedMotion = prefersReducedMotion(options.matchMedia);
  const fileInputId = `${id}-file`;
  const altInputId = `${id}-alt`;

  const handleFiles = async (files: FileList | File[] | null | undefined): Promise<void> => {
    if (!files) return;
    const list = Array.from(files as ArrayLike<File>);
    const first = list[0];
    if (!first) return;
    try {
      const uploaded = await uploader(first);
      onChange({
        asset_id: uploaded.id,
        url: uploaded.url,
        ...(uploaded.mime !== undefined ? { mime: uploaded.mime } : {}),
        ...(value.alt_text !== undefined ? { alt_text: value.alt_text } : {}),
      });
    } catch (err) {
      // Surfaced via the progress prop in the next render. The caller
      // decides whether to retry; we emit a console diagnostic.
      console.warn('asset upload failed:', err);
    }
  };

  const preview = (() => {
    if (!value.asset_id) {
      return span(
        {
          class: 'dl-asset-field-empty',
          ...(testid !== undefined ? { 'data-testid': `${testid}-empty` } : {}),
        },
        [messages.empty],
      );
    }
    if (value.url) {
      return img({
        src: value.url,
        alt: value.alt_text ?? '',
        class: 'dl-asset-field-thumb',
        ...(testid !== undefined ? { 'data-testid': `${testid}-thumb` } : {}),
      });
    }
    return div(
      {
        class: 'dl-asset-field-broken',
        role: 'alert',
        ...(testid !== undefined ? { 'data-testid': `${testid}-broken` } : {}),
      },
      [messages.broken],
    );
  })();

  const progressNode = (() => {
    if (progress.kind === 'idle') return null;
    if (progress.kind === 'error') {
      return div(
        {
          class: 'dl-asset-field-error',
          role: 'alert',
          ...(testid !== undefined ? { 'data-testid': `${testid}-error` } : {}),
        },
        [progress.message],
      );
    }
    const pct = Math.max(0, Math.min(100, Math.round(progress.percent)));
    return div(
      {
        class: reducedMotion
          ? 'dl-asset-field-progress dl-asset-field-progress--no-anim'
          : 'dl-asset-field-progress',
        role: 'progressbar',
        'aria-valuenow': String(pct),
        'aria-valuemin': '0',
        'aria-valuemax': '100',
        ...(testid !== undefined ? { 'data-testid': `${testid}-progress` } : {}),
      },
      [`${pct}%`],
    );
  })();

  const dropZone =
    mode === 'edit'
      ? label(
          {
            for: fileInputId,
            class: 'dl-asset-field-drop',
            onDrop: (event: DragEvent) => {
              event.preventDefault();
              void handleFiles(event.dataTransfer?.files);
            },
            onDragOver: (event: DragEvent) => {
              event.preventDefault();
            },
            onPaste: (event: ClipboardEvent) => {
              const items = event.clipboardData?.items;
              if (!items) return;
              const files: File[] = [];
              for (const item of items) {
                if (item.kind === 'file') {
                  const file = item.getAsFile();
                  if (file) files.push(file);
                }
              }
              if (files.length > 0) {
                event.preventDefault();
                void handleFiles(files);
              }
            },
            ...(testid !== undefined ? { 'data-testid': `${testid}-drop` } : {}),
          },
          [
            span({ class: 'dl-asset-field-drop-text' }, [messages.dropPrompt]),
            span({ class: 'dl-asset-field-choose' }, [messages.chooseFile]),
            input({
              id: fileInputId,
              type: 'file',
              accept: 'image/png,image/jpeg,image/webp,image/gif',
              class: 'dl-asset-field-input',
              onChange: (event: Event) => {
                const target = event.target as HTMLInputElement | null;
                void handleFiles(target?.files);
              },
            }),
          ],
        )
      : null;

  const removeButton =
    mode === 'edit' && value.asset_id
      ? button(
          {
            type: 'button',
            class: 'dl-asset-field-remove',
            onClick: () => {
              onChange({ asset_id: null, url: null });
            },
            ...(testid !== undefined ? { 'data-testid': `${testid}-remove` } : {}),
          },
          [messages.remove],
        )
      : null;

  const altText =
    mode === 'edit'
      ? div({ class: 'dl-asset-field-alt' }, [
          label({ for: altInputId, class: 'dl-asset-field-alt-label' }, [messages.altTextLabel]),
          input({
            id: altInputId,
            type: 'text',
            value: value.alt_text ?? '',
            placeholder: messages.altTextPlaceholder,
            class: 'dl-asset-field-alt-input',
            onInput: (event: Event) => {
              const target = event.target as HTMLInputElement | null;
              const text = target?.value ?? '';
              onChange({
                asset_id: value.asset_id,
                url: value.url,
                ...(value.mime !== undefined ? { mime: value.mime } : {}),
                ...(text.length > 0 ? { alt_text: text } : {}),
              });
            },
            ...(testid !== undefined ? { 'data-testid': `${testid}-alt-input` } : {}),
          }),
        ])
      : null;

  const children: Mountable[] = [preview];
  if (progressNode) children.push(progressNode);
  if (dropZone) children.push(dropZone);
  if (removeButton) children.push(removeButton);
  if (altText) children.push(altText);

  return div(
    {
      class: 'dl-asset-field',
      role: 'group',
      'aria-label': messages.heading,
      ...(testid !== undefined ? { 'data-testid': testid } : {}),
    },
    children,
  );
}
